
import getConfig from 'next/config';

import React, { Fragment, Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { withRouter } from 'next/router';
import styled from 'styled-components';
import 'url-search-params-polyfill';

import '../../utils/createNonce'; // for CSP
import '../../utils/polyfills'; // for old browsers

import { decimalPlaces, formatNumber } from '../../utils/number';
import { login } from '../../utils/sensors';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReferralDataSetter from '../../components/ReferralDataSetter';

import { getPathWithoutLocale } from '../../utils/canonical';
import '../../utils/check';

require('../../static/css/base.css');
require('../../node_modules/react-tippy/dist/tippy.css');
require('../../node_modules/react-virtualized/styles.css');
require('../../static/css/iconfont.css');
require('../../static/css/nprogress.css');
require('../../static/css/overrides.css');

const {
  publicRuntimeConfig: {
    assetPrefix,
    publicAssetPrefix,
    brand,
  },
} = getConfig();

const HTTPS = 'https://';

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: ${({
    theme, isBisicTradePage,
  }) => isBisicTradePage ? theme.basictTradePageMinWidth : theme.minWidth};
  background: ${({ theme, isGreyBg }) => (isGreyBg ? theme.greyBg : theme.bg)};
  background: ${({ theme, isLoginBg }) => (isLoginBg ? theme.loginBg : '')};
`;

const mapStateToProps = ({
  tickers,
  depthSocketConnected,
  userInfo,
}) => ({
  tickers,
  depthSocketConnected,
  userInfo,
});

export default
@connect(mapStateToProps)
@withRouter
class Layout extends Component {
  static propTypes = {
    emitAmp: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    locale: PropTypes.string,
    metaData: PropTypes.shape({
      title: PropTypes.string.isRequired,
      keywords: PropTypes.string,
      description: PropTypes.string,
      ogImage: PropTypes.string,
    }),
    omitHeader: PropTypes.bool,
    omitFooter: PropTypes.bool,
    symbol: PropTypes.string,
    both: PropTypes.array,
    tickers: PropTypes.object,
    selectedPair: PropTypes.string,
    isGreyBg: PropTypes.bool,
    isBisicTradePage: PropTypes.bool,
    content: PropTypes.any,
    siteLocale: PropTypes.string,
    router: PropTypes.object,
    hostName: PropTypes.string,
    depthSocketConnected: PropTypes.bool,
    userInfo: PropTypes.object,
  };

  static defaultProps = {
    emitAmp: false,
    isLoggedIn: false,
    locale: 'en',
    metaData: {},
    // withTradingView: false,
    omitHeader: false,
    omitFooter: false,
    symbol: '',
    both: ['BTC', 'USDT'], // TODO: improve.
    tickers: {},
    selectedPair: 'BTC_USDT',
    isGreyBg: false,
    isBisicTradePage: false,
    content: null,
    siteLocale: 'global',
    router: { asPath: '/' },
    hostName: 'www.binance.com',
    depthSocketConnected: false,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const userId = nextProps.userInfo?.userData?.userId;
    if (prevState?.userId !== userId) {
      login(userId);
    }
    return {
      userId,
    };
  }

  render() {
    const {
      emitAmp,
      isLoggedIn,
      metaData,
      omitHeader,
      omitFooter,
      symbol,
      both,
      tickers,
      selectedPair,
      isGreyBg,
      isBisicTradePage,
      content,
      router,
      locale,
      hostName,
      depthSocketConnected,
    } = this.props;
    const url = `${HTTPS}${hostName}${router.asPath}`;

    const canonicalUrl = `${hostName}${router.asPath}`;
    const canonicalUrlWithProtocol = !canonicalUrl.startsWith(HTTPS)
      ? `${HTTPS}${canonicalUrl}`
      : canonicalUrl;
    const ampHtmlUrl = `${hostName}/${locale}/amp${getPathWithoutLocale(router.asPath)}`;
    const ampHtmlUrlWithProtocol = !ampHtmlUrl.startsWith(HTTPS) ? `${HTTPS}${ampHtmlUrl}` : ampHtmlUrl;
    const tickSize = tickers[symbol] ? decimalPlaces(tickers[symbol].tickSize) : null;
    const tickersPrice =
      tickers[symbol] && tickers[symbol].c && tickSize && formatNumber(tickers[symbol].c, tickSize);
    const ogImage = metaData.ogImage || `${publicAssetPrefix}/static/images/common/ogImage.jpg`;
    return (
      <Fragment>
        <Head>
          <title>
            {tickersPrice && depthSocketConnected ? `${tickersPrice} | ` : ''}
            {metaData.title}
          </title>
          <link
            rel="shortcut icon"
            type="image/x-icon"
            href={`${assetPrefix}/static/images/common/favicon.ico`}
          />
          <link href={canonicalUrlWithProtocol} rel="canonical" />
          {
            emitAmp ? <link href={ampHtmlUrlWithProtocol} rel="amphtml" /> : null
          }
          {metaData.keywords ? <meta name="keywords" content={metaData.keywords} /> : null}
          <meta property="og:url" content={url} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={metaData.title} />
          <meta property="og:site_name" content={brand} />
          <meta property="og:image" content={ogImage} />
          <meta property="twitter:title" content={metaData.title} />
          <meta property="twitter:site" content={brand} />
          <meta property="twitter:image" content={ogImage} />
          <meta property="twitter:image:src" content={ogImage} />
          <meta property="twitter:card" content="summary_large_image" />
          {metaData.description ? (
            <Fragment>
              <meta name="description" content={metaData.description} />
              <meta property="og:description" content={metaData.description} />
              <meta property="twitter:description" content={metaData.description} />
            </Fragment>
          ) : null}
          {/* needed for the SSR cache, do not remove! */}
          <meta httpEquiv="X-User-Session-Validity" content={isLoggedIn ? '-SESSION_VALID-' : ''} />
        </Head>
        <StyledLayout isGreyBg={isGreyBg} className="layout" isBisicTradePage={isBisicTradePage}>
          <ReferralDataSetter />
          {!omitHeader && (
            <Header
              isLoggedIn={isLoggedIn}
              both={both}
              origin={`https://${hostName}`}
              selectedPair={selectedPair}
            />
          )}
          {content}
          {!omitFooter && (
            <Footer />
          )}
        </StyledLayout>
      </Fragment>
    );
  }
}
